import { z } from 'zod';

import { StatusTypes } from 'api/traps/types';

export interface FloorPlan {
  id: number;
  title: string;
  slug: string;
  scale: number;
  area: string;
  floor_plan: string;
  add_date: string;
}

export interface UnmountedMCT {
  id: number;
  number: number;
  serial_number: string;
  model: string;
  slug: string;
}

export interface InteractiveMapPlan {
  title: string;
  uploaded_file: File;
  site_slug: string;
  slug: string;
}
export interface InteractiveMapPlanDelete {
  site_slug: string;
  slug: string;
}

export type UnmountedMCTResponse = UnmountedMCT[];

export type FloorPlansResponse = FloorPlan[];

export type FloorPlanResponse = FloorPlan;

export interface Activity {
  date_time: string | null;
  amount?: string;
  snap_status?: string | null;
}

export interface HistoryTrigger {
  date_time: string;
  enter: string;
  bait_eaten?: number | null;
  status?: StatusTypes | null;
}
export enum TrapStatus {
  dark = 'dark',
  danger = 'danger',
  warning = 'warning',
  success = 'success',
}
export interface Device {
  serial_number: string;
  model_id: number;
  bait_filling: string;
  status: TrapStatus | string;
  installment_comment: string | null;
  battery_percentage: number;
  caught_pests: number | null;
  floor_number_id: number;
  hub_id: number;
  id: number;
  last_connect: string | null;
  model: string;
  number: number;
  object_id: number;
  percentage_of_filling: number;
  pest_company_id: number;
  slug: string;
  x: number;
  y: number;
  sequence_number: number;
  last_maintenance: string | null;
  trap_connected_module?: string | null;
  snap?: number;
  last_capture_1?: string | null;
  last_capture_2?: string | null;
  last_empty_1?: string | null;
  last_empty_2?: string | null;
  history_triggers?: HistoryTrigger[];
  combine_history_triggers?: HistoryTrigger[];
  place_point_id: number | null;
}

export interface UnmountedHub {
  id: number;
  serial_number: string;
  number: number;
  model: string;
  pest_company_id: number;
  object_id: number;
  slug: string;
}

export type UnmountedHubResponse = UnmountedHub[];

export interface DevicesOnMapResponse {
  mct: Device[];
  srst: Device[];
  smst: Device[];
  srbm: Device[];
}

export interface UnmountedSigns {
  vul: VulnerabilityOnMap[];
  enc: EncounterOnMap[];
}

export type UnmountedSignsResponse = UnmountedSigns;

export const changeTrapLocationSchema = z.object({
  x_coord: z.number().min(0, { message: 'X coordinate must be greater than 0' }),
  y_coord: z.number().min(0, { message: 'Y coordinate must be greater than 0' }),
});

export type ChangeTrapLocationFormValues = z.infer<typeof changeTrapLocationSchema>;

export const addPlanSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  uploaded_file: z.instanceof(File),
  area: z.string().optional(),
});

export type AddPlanFormValues = z.infer<typeof addPlanSchema>;

export interface HubsOnMap {
  last_connect: string;
  serial_number: string;
  sequence_number: number;
  model_id: number;
  firmware: string | null;
  date_of_installation: string;
  installment_comment: string;
  pest_company_id: number;
  object_id: number;
  host: string | null;
  mac: string | null;
  web_server: string | null;
  version: string | null;
  is_archived: boolean;
  archived_date: string | null;
  slug: string;
  id: number;
  x: number;
  y: number;
}

export type HubsOnMapResponse = HubsOnMap[];

export interface Location {
  id: number;
  title: string;
  number: number;
  coordinates: [number, number][];
  color?: string;
}

export type LocationsOnMapResponse = Location[];
export interface HeatMapData {
  x: number;
  y: number;
  count: number;
}

export type HeatMapDataResponse = HeatMapData[];

export interface VulnerabilityOnMap {
  object_id: number;
  create_date: string;
  service: string | null;
  category: string;
  criticality: string;
  pest_id: number | null;
  zone_id: number | null;
  discovered: string;
  inspector_id: number;
  x_coord: number;
  y_coord: number;
  responsible: string;
  last_date: string;
  fix_date: string | null;
  poison: string | null;
  comment: string;
  fixed: boolean;
  fix_comment: string | null;
  floor_plan_id: number;
  mark_ad_done_person_id: number | null;
  is_archived: boolean;
  id: number;
}

export type VulnerabilitiesOnMapResponse = VulnerabilityOnMap[];

export interface EncounterOnMap {
  report_object_id: number;
  enc_type: string;
  discovery_date: string;
  discovery_time: string;
  place: string;
  pest_id: number | null;
  amount: number;
  pest_traces: string;
  witness: string;
  inspection_date: string | null;
  inspection_time: string | null;
  inspector_id: number;
  actions_to_correct: string | null;
  comment: string;
  x_coord: number;
  y_coord: number;
  floor_plan_id: number;
  fix_comment: string | null;
  fix_person_id: number | null;
  id: number;
  photos: string[];
}

export type EncountersOnMapResponse = EncounterOnMap[];

export type HeatMapSBSSignal = [number, number, number];

export type HeatMapSBSDataResponse = HeatMapSBSSignal[];

export const vulnerabilityCreateSchema = z.object({
  category: z.string().min(1, { message: 'Category is required' }),
  criticality: z.string().min(1, { message: 'Criticality is required' }),
  discovered: z.string().min(1, { message: 'Discovered is required' }),
  responsible: z.string().min(1, { message: 'Responsible is required' }),
  comment: z.string().min(1, { message: 'Comment is required' }),
  x_coord: z.number({ required_error: 'X coordinate is required' }),
  y_coord: z.number({ required_error: 'Y coordinate is required' }),
  floor_plan_slug: z.string().min(1, { message: 'Floor plan slug is required' }),
  create_date: z.string().min(1, { message: 'Fix date and time is required' }),
  before_photos: z.array(z.instanceof(File)).optional(),
});

export type VulnerabilityCreateFormValues = z.infer<typeof vulnerabilityCreateSchema>;
const baseEncounterSchema = z.object({
  floor_plan: z.string().optional().nullable(),
  x_coord: z.number({ required_error: 'X coordinate is required' }).nullable(),
  y_coord: z.number({ required_error: 'Y coordinate is required' }).nullable(),
  discovery_datetime: z.string(),
  place: z.string({ required_error: 'Place is required' }).min(1, { message: 'Place is required' }),
  pest: z.number().nullable(),
  amount: z.number().nullable(),
  pest_traces: z.number().nullable(),
  witness: z.number({ required_error: 'Witness is required' }).refine(val => val !== null, {
    message: 'Witness is required',
  }),
  creation_comment: z.string().optional(),
  is_blitz_creation: z.boolean().default(true),
  photos: z.array(z.instanceof(File)).optional().nullable(),
});
const applyPestRefinements = <T extends z.ZodTypeAny>(schema: T) => {
  return schema
    .refine(
      data => {
        if (data.pest !== null) return data.amount !== null;
        return true;
      },
      { message: 'Amount is required if pest is selected', path: ['amount'] },
    )
    .refine(
      data => {
        if (data.pest === null) return data.pest_traces !== null;
        return true;
      },
      { message: 'Pest traces are required if pest is not selected', path: ['pest_traces'] },
    )
    .refine(
      data => {
        if (data.pest_traces === null) return data.pest !== null;
        return true;
      },
      { message: 'Pest is required if pest traces are not selected', path: ['pest'] },
    );
};
export const encounterCreateSchema = applyPestRefinements(
  baseEncounterSchema.extend({
    responsible_person: z
      .number({ required_error: 'Responsible person is required' })
      .nullable()
      .refine(val => val !== null, {
        message: 'Responsible person is required',
      }),
  }),
);

export const encounterDraftSchema = applyPestRefinements(
  baseEncounterSchema.extend({
    responsible_person: z.number().nullable().optional(),
  }),
);
export type EncounterCreateFormValues = z.infer<typeof encounterCreateSchema>;
export type EncounterDraftFormValues = z.infer<typeof encounterDraftSchema>;

export const editPlanOnMapSchema = z.object({
  title: z.string().min(1, { message: 'Plan name is required' }),
  uploaded_file: z.instanceof(File),
});
export type EditPlanOnMapFormValues = z.infer<typeof editPlanOnMapSchema>;

export enum SignType {
  vulnerability = 'vul',
  sightings = 'enc',
}
export const addLocationSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  number: z.string().min(1, { message: 'Number of devices must be at least 1' }),
  coordinates: z
    .array(z.tuple([z.number(), z.number()]))
    .min(3, { message: 'At least 3 coordinates are required to form a polygon' }),
  color: z.string().min(1, { message: 'Color is required' }),
});
export type AddLocationFormValues = z.infer<typeof addLocationSchema>;

export type DeviceStatus = 'success' | 'warning' | 'danger' | 'dark' | string;
export type EnterDirection = 'L' | 'R' | string;
export type TriggerStatus = 'approved_mice' | 'caught_empty' | 'approved_not_mice' | 'consumption' | string;

export interface CatchActivity {
  label: string;
  caught: number;
  not_caught: number;
}

export interface BaitActivity {
  label: string;
  bait_eaten: number;
}

export interface CatchHistoryTrigger {
  date_time: string;
  status: TriggerStatus;
  enter: EnterDirection;
}

export interface BaitHistoryTrigger {
  date_time: string;
  status: TriggerStatus;
  bait_eaten: number;
}

export interface BaseDevice {
  id: number;
  serial_number: string;
  model_id: number;
  status: DeviceStatus;
  last_connect: string | null;
  battery_percentage: number;
  hub_id: number;
  object_id: number;
  pest_company_id: number;
  floor_number_id: number;
  slug: string;
  installment_comment: string | null;
  x: number;
  y: number;
  model: string;
  number: number;
  last_maintenance: string | null;
  is_current_position: boolean;
  percentage_of_filling: number | null;
  bait_filling?: string | number;
  sequence_number?: number;
}

export interface MCTDevice extends BaseDevice {
  caught_pests: number;
  activity: CatchActivity;
  history_triggers: CatchHistoryTrigger[];
}

export interface SnapTrapDevice extends BaseDevice {
  trap_connected_module: EnterDirection;
  activity: CatchActivity;
  history_triggers: CatchHistoryTrigger[];
}

export interface BaitMonitorDevice extends BaseDevice {
  bait_filling: number;
  activity: BaitActivity;
  history_triggers: BaitHistoryTrigger[];
}

export interface ArchiveMeta {
  mode: 'date' | 'period' | string;
  map_date: string | null;
  selected_date: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface ArchiveDevicesResponse {
  mct: MCTDevice[];
  srbm: BaitMonitorDevice[];
  srst: SnapTrapDevice[];
  smst: SnapTrapDevice[];
}

export interface ArchiveVulnerability {
  id: number;
  object_id: number;
  create_date: string;
  service: string | null;
  category: string;
  criticality: string;
  pest_id: number | null;
  zone_id: number | null;
  discovered_id: number;
  inspector_id: number;
  x_coord: number;
  y_coord: number;
  responsible_id: number;
  last_date: string;
  fix_date: string | null;
  poison: string | null;
  comment: string;
  is_fixed: boolean;
  fix_comment: string | null;
  floor_plan_id: number;
  mark_ad_done_person_id: number | null;
  is_archived: boolean;
  is_current_position: boolean;
}

export interface EncounterUser {
  name: string;
  avatar: string;
}

export interface EncounterPest {
  id: number;
  name_US: string;
  name_UA: string;
}

export interface ArchiveEncounter {
  id: number;
  report_object_id: number;
  floor_plan_id: number;
  x_coord: number;
  y_coord: number;
  created_at: string;
  updated_at: string;
  witness_role: string;
  responsible_person_role: string;
  enc_type: string;
  place: string;
  pest_id: number;
  amount: number;
  pest_traces_id: number | null;
  discovery_datetime: string;
  witness_id: number;
  responsible_person_id: number;
  inspector_id: number | null;
  creation_comment: string;
  fix_comment: string | null;
  fix_person_id: number | null;
  fix_datetime: string | null;
  should_client_fix: boolean;
  is_fixed: boolean;
  is_blitz_creation: boolean;
  before_photos: any[];
  after_photos: any[];
  pest: EncounterPest;
  pest_traces: any | null;
  witness: EncounterUser;
  responsible_person: EncounterUser;
  is_current_position: boolean;
}

export interface FullArchiveMapResponse {
  archive: ArchiveMeta;
  traps: ArchiveDevicesResponse;
  markers: {
    vulnerabilities: ArchiveVulnerability[];
    encounters: ArchiveEncounter[];
  };
}
